@if (open()) {
	<div class="gpu-worker-backdrop" (click)="close()">
		<section
			class="gpu-worker-dialog"
			role="dialog"
			aria-modal="true"
			aria-labelledby="gpu-worker-title"
			aria-describedby="gpu-worker-description"
			tabindex="-1"
			(click)="$event.stopPropagation()"
			(keydown.escape)="close()">
			<header class="gpu-worker-dialog__header">
				<div>
					<p class="gpu-worker-dialog__eyebrow">Compute settings</p>
					<h2 id="gpu-worker-title">Simulation mode</h2>
					<p id="gpu-worker-description" class="gpu-worker-dialog__description">
					Choose how simulations run across all projects.
					</p>
				</div>
				<div class="gpu-worker-dialog__header-actions">
					@if (jobTimingHistoryUrl(); as timingUrl) {
						<a class="gpu-worker-status-card__link gpu-worker-status-card__link--header" [href]="timingUrl" target="_blank" rel="noopener noreferrer">
							Timing history dashboard
						</a>
					}
					<button class="gpu-worker-dialog__close" type="button" aria-label="Close simulation mode dialog" (click)="close()">
						×
					</button>
				</div>
			</header>

			<section class="gpu-worker-toolbar" aria-label="Worker filters and status">
				<div class="gpu-worker-toolbar__group">
					<div class="gpu-worker-toolbar__segmented" role="group" aria-label="Select compute type">
						<button
							type="button"
							[class.gpu-worker-toolbar__segment--active]="executionPreference() === 'vps'"
							(click)="setExecutionPreference('vps')">
							CPU
						</button>
						<button
							type="button"
							[class.gpu-worker-toolbar__segment--active]="executionPreference() === 'runpod'"
							(click)="setExecutionPreference('runpod')">
							GPU
						</button>
					</div>
				</div>

				@if (lastRefreshedLabel(); as refreshedAt) {
					<p class="gpu-worker-toolbar__last-checked">Last checked {{ refreshedAt }}</p>
				}

				<div class="gpu-worker-toolbar__actions">
					<button type="button" class="gpu-worker-btn gpu-worker-btn--secondary" (click)="refresh()" [disabled]="loading()">
						Refresh
					</button>
					<button type="button" class="gpu-worker-btn gpu-worker-btn--primary" (click)="close()">
						Close
					</button>
				</div>
			</section>

			<div class="gpu-worker-dialog__body">
				@if (loadError()) {
					<p class="gpu-worker-status gpu-worker-status--error">{{ loadError() }}</p>
				} @else if (loading()) {
					<p class="gpu-worker-status gpu-worker-status--info">Loading worker status…</p>
				} @else if (executionPreference() === 'vps') {
					<p class="gpu-worker-status gpu-worker-status--info">CPU mode is active. Simulations run on VPS compute.</p>
				} @else if (runpodProvider(); as provider) {
					@if (!provider.configured) {
						<div class="gpu-worker-status-card">
							<div class="gpu-worker-status-card__dot"></div>
							<div class="gpu-worker-status-card__content">
								<strong class="gpu-worker-status-card__label">Not configured</strong>
								<p class="gpu-worker-status-card__desc">The GPU backend API key is not set up. Contact your administrator before submitting simulation jobs.</p>
							</div>
						</div>
					} @else if (runpodEndpointStatuses().length === 0) {
						<p class="gpu-worker-status gpu-worker-status--info">No Runpod GPU endpoints found. Hit Refresh to check again.</p>
					} @else {
						<div class="gpu-worker-endpoints">
							@for (endpoint of runpodEndpointStatuses(); track endpoint.id) {
								<div
									class="gpu-worker-endpoint-card"
									[class.gpu-worker-endpoint-card--healthy]="endpoint.healthStatus === 'healthy'"
									[class.gpu-worker-endpoint-card--degraded]="endpoint.healthStatus === 'degraded'"
									[class.gpu-worker-endpoint-card--offline]="endpoint.healthStatus === 'offline'">
									<div class="gpu-worker-endpoint-card__header">
										<div class="gpu-worker-endpoint-card__title-wrap">
											<strong>{{ endpoint.name }}</strong>
											<span class="gpu-worker-endpoint-card__id">{{ endpoint.id }}</span>
										</div>
										<span class="gpu-worker-endpoint-card__badge">{{ healthStatusLabel(endpoint.healthStatus) }}</span>
									</div>

									<div class="gpu-worker-endpoint-card__metrics">
										<div class="gpu-worker-endpoint-card__metric">
											<span>Ready</span>
											<strong>{{ endpoint.workersReady }}</strong>
										</div>
										<div class="gpu-worker-endpoint-card__metric">
											<span>Idle</span>
											<strong>{{ endpoint.workersIdle }}</strong>
										</div>
										<div class="gpu-worker-endpoint-card__metric">
											<span>Running</span>
											<strong>{{ endpoint.workersInProgress }}</strong>
										</div>
										<div class="gpu-worker-endpoint-card__metric">
											<span>Queued</span>
											<strong>{{ endpoint.jobsQueued }}</strong>
										</div>
									</div>

									<div class="gpu-worker-endpoint-card__details">
										<p><span>GPU</span><strong>{{ gpuLabel(endpoint) }}</strong></p>
										<p><span>Worker pool</span><strong>{{ workerPoolLabel(endpoint) }}</strong></p>
										<p><span>Template</span><strong>{{ endpoint.templateId || 'Not reported' }}</strong></p>
									</div>

									@if (endpoint.healthError) {
										<p class="gpu-worker-endpoint-card__error">Health check issue: {{ endpoint.healthError }}</p>
									}
								</div>
							}
						</div>
					}
				} @else {
					<p class="gpu-worker-status gpu-worker-status--info">No GPU provider found. Hit Refresh to check again.</p>
				}
			</div>
		</section>
	</div>
}